import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

const normalizeTelegramInput = (value) => value.trim().replace(/^@+/, "");

const TelegramAccountModal = () => {
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const [telegramUsername, setTelegramUsername] = useState("");
  const [saving, setSaving] = useState(false);

  const requiresTelegramConfirmation = ["customer", "user"].includes(user?.role);
  const needsTelegramConfirmation = Boolean(
    user && requiresTelegramConfirmation && (!user.telegram_confirmed || !user.telegram_username)
  );

  useEffect(() => {
    if (needsTelegramConfirmation) {
      setTelegramUsername(normalizeTelegramInput(user?.telegram_username || ""));
    }
  }, [needsTelegramConfirmation, user?.telegram_username]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanUsername = normalizeTelegramInput(telegramUsername);
    if (!/^[A-Za-z0-9_]{5,32}$/.test(cleanUsername)) {
      toast.error(
        language === "tr"
          ? "Telegram kullanıcı adı 5-32 karakter olmalı. Sadece harf, rakam ve alt çizgi kullanın."
          : "Telegram username must be 5-32 characters. Use only letters, numbers and underscores."
      );
      return;
    }

    setSaving(true);
    try {
      const response = await api.put("/auth/telegram", { telegram_username: cleanUsername });
      setUser(response.data);
      toast.success(language === "tr" ? "Telegram hesabınız onaylandı." : "Telegram account confirmed.");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Telegram hesabı kaydedilemedi" : "Could not save Telegram account"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={needsTelegramConfirmation} onOpenChange={() => {}}>
      <DialogContent className="glass border-white/10 [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            {language === "tr" ? "Telegram hesabınızı onaylayın" : "Confirm your Telegram account"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {language === "tr"
              ? "Sipariş bildirimleri ve hesap doğrulaması için Telegram kullanıcı adınız zorunludur. Devam etmek için kullanıcı adınızı girin."
              : "Your Telegram username is required for order notifications and account verification. Enter it to continue."}
          </p>
          <div className="space-y-2">
            <Label htmlFor="telegram_username" className="font-mono text-sm">
              {language === "tr" ? "Telegram kullanıcı adı" : "Telegram username"}
            </Label>
            <div className="flex">
              <span className="inline-flex items-center border border-r-0 border-white/10 bg-black/50 px-3 font-mono text-muted-foreground">@</span>
              <Input
                id="telegram_username"
                value={telegramUsername}
                onChange={(e) => setTelegramUsername(normalizeTelegramInput(e.target.value))}
                placeholder="rootseo_user"
                className="bg-black/50 border-white/10 rounded-none font-mono"
                autoFocus
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {language === "tr" ? "Örnek: @rootseo_user" : "Example: @rootseo_user"}
            </p>
          </div>
          <Button type="submit" disabled={saving} className="w-full font-mono uppercase rounded-none">
            {saving
              ? (language === "tr" ? "Kaydediliyor..." : "Saving...")
              : (language === "tr" ? "Onayla ve devam et" : "Confirm and continue")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TelegramAccountModal;
